import type { GameState } from '../game/GameState.ts';

/**
 * The event vocabulary.
 *
 * The six STIM/BUTTON/FLAP types in the middle are the reason this project
 * exists. They spell out the pipeline explicitly, one timestamp per stage:
 *
 *   BOT_FLAP_REQUESTED   the controller formed an intent
 *          |             <- decision -> request
 *   STIM_REQUESTED       the software asked the stimulator for a pulse
 *          |             <- software / transport latency
 *   STIM_STARTED         the stimulator reported the pulse beginning
 *   STIM_ENDED           ... and ending
 *          |             <- physiological latency
 *   BUTTON_DOWN          a real input transition was detected
 *          |             <- input -> simulation quantisation
 *   FLAP_EXECUTED        the game actually applied a flap impulse
 *
 * Anything that breaks that chain gets its own terminal event
 * (STIM_REJECTED, STIM_MISSED, INPUT_FALSE_POSITIVE) so no request silently
 * disappears from the analysis.
 */
export const EventType = {
  // session / trial lifecycle
  SESSION_START: 'SESSION_START',
  SESSION_END: 'SESSION_END',
  TRIAL_START: 'TRIAL_START',
  TRIAL_END: 'TRIAL_END',
  CONFIG_CHANGED: 'CONFIG_CHANGED',
  MODE_CHANGED: 'MODE_CHANGED',
  CONTROLLER_CHANGED: 'CONTROLLER_CHANGED',

  // game
  GAME_RESET: 'GAME_RESET',
  /** Periodic state snapshot, emitted at `stateSampleHz`, for trajectory plots. */
  GAME_SAMPLE: 'GAME_SAMPLE',
  GAME_OVER: 'GAME_OVER',
  SCORE: 'SCORE',
  PAUSED: 'PAUSED',
  RESUMED: 'RESUMED',

  // the control pipeline
  BOT_FLAP_REQUESTED: 'BOT_FLAP_REQUESTED',
  STIM_REQUESTED: 'STIM_REQUESTED',
  STIM_STARTED: 'STIM_STARTED',
  STIM_ENDED: 'STIM_ENDED',
  BUTTON_DOWN: 'BUTTON_DOWN',
  BUTTON_UP: 'BUTTON_UP',
  FLAP_EXECUTED: 'FLAP_EXECUTED',

  // pipeline failures
  STIM_REJECTED: 'STIM_REJECTED',
  STIM_FAILED: 'STIM_FAILED',
  STIM_MISSED: 'STIM_MISSED',
  INPUT_FALSE_POSITIVE: 'INPUT_FALSE_POSITIVE',

  // EMG
  EMG_THRESHOLD_CROSSED: 'EMG_THRESHOLD_CROSSED',
  EMG_BELOW_THRESHOLD: 'EMG_BELOW_THRESHOLD',

  // safety
  STIM_ARMED: 'STIM_ARMED',
  STIM_DISARMED: 'STIM_DISARMED',
  EMERGENCY_STOP: 'EMERGENCY_STOP',
  STIMULATOR_CONNECTED: 'STIMULATOR_CONNECTED',
  STIMULATOR_DISCONNECTED: 'STIMULATOR_DISCONNECTED',

  // calibration
  CALIBRATION_START: 'CALIBRATION_START',
  CALIBRATION_TRIAL: 'CALIBRATION_TRIAL',
  CALIBRATION_END: 'CALIBRATION_END',

  // derived measurements
  LATENCY_SAMPLE: 'LATENCY_SAMPLE',
} as const;

export type EventTypeName = (typeof EventType)[keyof typeof EventType];

export interface ExperimentEvent {
  sessionId: string;
  trialId?: string;

  /** performance.now(), ms, sub-millisecond resolution. */
  timestamp: number;
  tick: number;

  type: string;

  /**
   * Simulation clock at the time of the event, ms. Unlike `timestamp` this is
   * free of frame-rate jitter, which makes it the right x-axis for plots.
   */
  simTimeMs?: number;

  gameState?: GameState;

  /**
   * Correlation id linking every stage of one intent -> flap chain.
   * Present on BOT_FLAP_REQUESTED, STIM_*, and the BUTTON_DOWN / FLAP_EXECUTED
   * that were attributed to that request.
   */
  requestId?: string;

  metadata?: Record<string, unknown>;
}
